import * as http from './http';
import {
  ApiResponse,
  EcomProduct,
  ListSort,
  PaginatedApiResponse,
  TestDriveVehicle,
  Vehicle,
  VehicleStatus,
  VehicleCeilingPrice,
  VehicleModelss,
  VehicleVarient,
  InventoryStatus,
  ProgressCode,
  Checklist,
} from '../models';
import ApiService from './service';
import { stringify } from 'query-string';
import { Dict } from '../../models';
import {
  CombinationPriceUploadResponse,
  LeadConfiguration,
} from '../models/configuration';

export class VehicleService extends ApiService {
  public async getVINData(
    sku: string,
    isPreVin?: boolean
  ): Promise<{
    available: Vehicle[];
    cannibalized: Vehicle[];
    accessorized: Vehicle[];
    marketing: Vehicle[];
    specialRequest: Vehicle[];
    display: Vehicle[];
    damaged: Vehicle[];
    reserved: Vehicle[];
    missingFeatures: Vehicle[];
  }> {
    const url = `${this.apiDomain}/api/vehicle/vin?${stringify({
      sku,
      isPreVin,
    })}`;
    const response = await http.get<
      ApiResponse<{
        available: Vehicle[];
        cannibalized: Vehicle[];
        accessorized: Vehicle[];
        marketing: Vehicle[];
        specialRequest: Vehicle[];
        display: Vehicle[];
        damaged: Vehicle[];
        reserved: Vehicle[];
        missingFeatures: Vehicle[];
      }>
    >(url, this.store);
    return response?.data?.data;
  }

  public async getVehicleList(
    data: {
      page?: number;
      search?: string;
      perPage?: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Vehicle>> {
    const url = `${this.apiDomain}/api/vehicle/list`;
    const response = await http.post<PaginatedApiResponse<Vehicle>>(
      url,
      data,
      this.store
    );
    return response?.data;
  }

  public async getVehicleDetails(
    id: string
  ): Promise<{ vehicle: Vehicle; configuration?: LeadConfiguration }> {
    const url = `${this.apiDomain}/api/vehicle/${id}`;
    const response = await http.get<
      ApiResponse<{ vehicle: Vehicle; configuration?: LeadConfiguration }>
    >(url, this.store);
    return response?.data?.data;
  }

  public async sycVehicleDetails(vsbNumber: string): Promise<ApiResponse<any>> {
    const url = `${this.apiDomain}/api/vehicle/autoline/enable-vehicle`;
    const response = await http.put<ApiResponse<any>>(
      url,
      { vsbNumber },
      this.store
    );
    return response?.data;
  }

  public async updateDisplayStatus(
    id: string,
    data: {
      vehicleStatus: VehicleStatus[];
      comment?: string;
      checkList?: Checklist[];
    }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/vehicle/${id}/vehicle-status`;
    const response = await http.put<ApiResponse>(url, data, this.store);
    return response?.data?.data;
  }

  public async updateTraderDetails(
    id: string,
    data: {
      isTraderVin: boolean;
      gmReportedDate?: Date;
      registrationDeadline?: Date;
    }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/vehicle/${id}/vehicle-trader-vin`;
    const response = await http.put<ApiResponse>(url, data, this.store);
    return response?.data?.data;
  }

  public async downloadVehicleList(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/vehicle/list/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Vehicles-${new Date()}.xlsx`
    );
  }

  async downloadVehicleReport(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/vehicle/report/download`;
    const response = await http.downloadPostData(url, data, this.store);

    http.saveDownloadedFile(
      response.data,
      'application/application/vnd.openxmlformats',
      `Vehicle-Report-${new Date()}.xlsx`
    );

    // return response.data;
  }

  public async downloadPreVinsList(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/pre-vin/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `PreVins-${new Date()}.xlsx`
    );
  }

  public async getTestDriveVehicleList(
    data?: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<TestDriveVehicle>> {
    const url = `${this.apiDomain}/api/vehicle/test-drive/list`;
    const response = await http.post<PaginatedApiResponse<TestDriveVehicle>>(
      url,
      data,
      this.store
    );
    return response?.data;
  }

  public async getTestDriveVehicleDetails(
    id: string
  ): Promise<TestDriveVehicle> {
    const url = `${this.apiDomain}/api/vehicle/test-drive/${id}`;
    const response = await http.get<ApiResponse<TestDriveVehicle>>(
      url,
      this.store
    );
    return response?.data?.data;
  }

  public async addTestDriveVehicle(
    data: TestDriveVehicle
  ): Promise<TestDriveVehicle> {
    const url = `${this.apiDomain}/api/vehicle/test-drive/create`;
    const response = await http.post(url, data, this.store);
    return response?.data;
  }

  public async updateTestDriveVehicle(
    data: TestDriveVehicle,
    id: string
  ): Promise<TestDriveVehicle> {
    const url = `${this.apiDomain}/api/vehicle/test-drive/${id}`;
    const response = await http.put(url, data, this.store);
    return response?.data;
  }

  public async deleteTestDriveVehicle(
    id: string
  ): Promise<TestDriveVehicle | unknown> {
    const url = `${this.apiDomain}/api/vehicle/test-drive/${id}`;
    const response = await http.remove(url, this.store);
    return response?.data ? response.data : '';
  }

  public async downloadTestDriveVehicleList(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/vehicle/test-drive/list/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Test-Drive-Vehicles-${new Date()}.xlsx`
    );
  }

  public async updateModelImage(
    id: string,
    data: { imageUrl: string }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/vehicle/vehicle-model/${id}/image`;
    const response = await http.put<ApiResponse>(url, data, this.store);
    return response?.data?.data;
  }

  public async getCurrentNpc(): Promise<VehicleCeilingPrice> {
    const url = `${this.apiDomain}/api/vehicle/net-price-ceiling/current`;
    const response = await http.get<ApiResponse<VehicleCeilingPrice>>(
      url,
      this.store
    );
    return response?.data?.data;
  }
  public async getVehicleSingleDetails(id: string): Promise<Vehicle> {
    const url = `${this.apiDomain}/api/vehicle/details/${id}`;
    const response = await http.get<ApiResponse<Vehicle>>(url, this.store);
    return response?.data?.data;
  }

  public async getVehicleModelsList(): Promise<string[]> {
    const url = `${this.apiDomain}/api/vehicle/models/list`;
    const response = await http.get<ApiResponse<string[]>>(url, this.store);
    return response?.data?.data;
  }
  public async addVehicleModels(
    data: VehicleModelss
  ): Promise<ApiResponse<VehicleModelss>> {
    const url = `${this.apiDomain}/api/vehicle/vehicle-model`;
    const response = await http.post(url, data, this.store);
    return response;
  }

  public async updateVehicleModels(
    data: VehicleModelss,
    id: string
  ): Promise<any> {
    const url = `${this.apiDomain}/api/vehicle/vehicle-model/${id}`;
    const response = await http.put(url, data, this.store);

    return response;
  }
  public async addVehicleVarient(
    data: VehicleVarient
  ): Promise<ApiResponse<VehicleVarient>> {
    const url = `${this.apiDomain}/api/vehicle/vehicle-variant`;
    const response = await http.post(url, data, this.store);
    return response;
  }
  public async updateVehicleVarient(
    data: VehicleVarient,
    id: string
  ): Promise<ApiResponse<VehicleVarient>> {
    const url = `${this.apiDomain}/api/vehicle/vehicle-variant/${id}`;
    const response = await http.put(url, data, this.store);
    return response;
  }

  public async getCombinationsList(
    data: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Vehicle>> {
    const url = `${this.apiDomain}/api/combinations/list`;
    const response = await http.post<PaginatedApiResponse<Vehicle>>(
      url,
      data,
      this.store
    );
    return response?.data;
  }

  public async downloadCombinationsList(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/combinations/list/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      'application/application/vnd.openxmlformats',
      `Vehicles-${new Date()}.xlsx`
    );
  }

  public async updateInteriorColor(
    id: string,
    data: { colorHex: string; image?: string }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/combinations/${id}/interior`;
    const response = await http.put<ApiResponse>(url, data, this.store);
    return response?.data?.data;
  }

  public async updateExteriorColor(
    id: string,
    data: { colorHex: string; image?: string }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/combinations/${id}/exterior`;
    const response = await http.put<ApiResponse>(url, data, this.store);
    return response?.data?.data;
  }
  public async updateVehicleDeliveryStatus(
    id: string,
    data: {
      markAsDelivered: boolean;
      deliveredDate: Date;
      deliveredTime: string;
    }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/vehicle/${id}/update-status/delivery`;
    await http.put(url, data, this.store);
  }
  public async updateVehicleRegistrationStatus(
    id: string,
    data: {
      status: string;
      submittedDate: Date;
      registrationNo?: string;
    }
  ): Promise<void> {
    const url = `${this.apiDomain}/api/vehicle/${id}/update-status/registration`;
    await http.put(url, data, this.store);
  }
  public async getVehicleAutolineStatus(
    vsbNumber: string
  ): Promise<{
    autolineStatus?: InventoryStatus;
    autolineProgressCode?: ProgressCode;
  }> {
    const url = `${this.apiDomain}/api/vehicle/vehicle-autoline-status`;
    const response = await http.post<
      ApiResponse<{
        autolineStatus?: InventoryStatus;
        autolineProgressCode?: ProgressCode;
      }>
    >(url, { vsbNumber }, this.store);
    return response?.data?.data;
  }
  public async getReceiveDate(data: {} & Dict): Promise<string[]> {
    const url = `${this.apiDomain}/api/vehicle/receive-date`;
    const response = await http.post<ApiResponse<string[]>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
  public async getExpectedReceiveDate(data: {} & Dict): Promise<string[]> {
    const url = `${this.apiDomain}/api/vehicle/expected-receive-date`;
    const response = await http.post<ApiResponse<string[]>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }

  public async getCombinationPriceUploadDetails(
    id: string
  ): Promise<CombinationPriceUploadResponse> {
    const url = `${this.apiDomain}/api/vehicle-date-price/uploads/${id}`;

    const resposne = await http.get<CombinationPriceUploadResponse>(
      url,
      this.store
    );
    return resposne?.data;
  }
  public async loadCombinationPriceUploadList(page: number, perPage: number) {
    const url = `${this.apiDomain}/api/vehicle-date-price/uploads?page=${page}&perPage=${perPage}`;

    const resposne = await http.get<
      PaginatedApiResponse<CombinationPriceUploadResponse[]>
    >(url, this.store);
    return resposne?.data;
  }

  public async getStopSaleDate(data: {} & Dict): Promise<string[]> {
    const url = `${this.apiDomain}/api/vehicle/stop-sale-date`;
    const response = await http.post<ApiResponse<string[]>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
  public async getshipName(data: {} & Dict): Promise<string[]> {
    const url = `${this.apiDomain}/api/vehicle/shipnames`;
    const response = await http.post<ApiResponse<string[]>>(
      url,
      data,
      this.store
    );
    return response?.data?.data;
  }
}
